'use client';

import { Infinity, Lock, LayoutList } from 'lucide-react';
import { type FairtradeStatusFilter } from '@/lib/fairtrade-catalog';
import { cn } from '@/lib/utils';

const STATUS_CHIPS: {
  id: FairtradeStatusFilter;
  label: string;
  icon: typeof LayoutList;
}[] = [
  { id: 'all', label: 'Todos', icon: LayoutList },
  { id: 'limited', label: 'Limitado', icon: Lock },
  { id: 'unlimited', label: 'Ilimitado', icon: Infinity },
];

type FairtradeStatusFilterBarProps = {
  value: FairtradeStatusFilter;
  onChange: (status: FairtradeStatusFilter) => void;
  isDark?: boolean;
};

export default function FairtradeStatusFilterBar({
  value,
  onChange,
  isDark = true,
}: FairtradeStatusFilterBarProps) {
  return (
    <div
      className={cn(
        'inline-flex w-full max-w-full items-center gap-2 rounded-2xl border px-2.5 py-1.5 sm:w-auto',
        isDark
          ? 'border-sky-400/25 bg-gradient-to-r from-sky-500/10 to-transparent shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]'
          : 'border-sky-200/90 bg-gradient-to-r from-sky-50 to-white shadow-sm'
      )}
      role="group"
      aria-label="Filtrar por status"
    >
      <div
        className={cn(
          'inline-flex w-full flex-wrap items-center gap-0.5 rounded-full p-0.5 sm:w-auto sm:flex-nowrap',
          isDark ? 'bg-[#01012e]/80' : 'bg-white/90 ring-1 ring-sky-100'
        )}
      >
        {STATUS_CHIPS.map((item) => {
          const active = value === item.id;
          const Icon = item.icon;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => onChange(item.id)}
              className={cn(
                'inline-flex h-8 flex-1 items-center justify-center gap-1.5 rounded-full px-3 sm:flex-none',
                'font-poppins text-xs font-semibold transition-all duration-200',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/40',
                !isDark &&
                  !active &&
                  'text-slate-500 hover:bg-sky-50 hover:text-sky-800',
                !isDark && active && item.id === 'all' && 'bg-sky-600 text-white shadow-sm',
                !isDark &&
                  active &&
                  item.id === 'limited' &&
                  'bg-orange-500 text-white shadow-sm',
                !isDark &&
                  active &&
                  item.id === 'unlimited' &&
                  'bg-cyan-600 text-white shadow-sm',
                isDark &&
                  !active &&
                  '!text-blue-200/70 hover:bg-white/[0.04] hover:!text-sky-100',
                isDark &&
                  active &&
                  item.id === 'all' &&
                  'bg-sky-400/25 !text-sky-50 shadow-sm ring-1 ring-sky-400/40',
                isDark &&
                  active &&
                  item.id === 'limited' &&
                  'bg-orange-500/20 !text-orange-100 ring-1 ring-orange-400/30',
                isDark &&
                  active &&
                  item.id === 'unlimited' &&
                  'bg-cyan-500/20 !text-cyan-100 ring-1 ring-cyan-400/30'
              )}
              aria-pressed={active}
            >
              <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
              {item.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
